import { Component, OnInit, OnDestroy, ViewChild, TemplateRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DataGateService } from '../../shared/services/datagate.service';
import { DataGateResponse, ProjectSettings } from '../../shared/models/datagate/datagate.model';
import { ToastrService } from 'ngx-toastr';
import { CoreService } from "../../shared/services/core.service";
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ProjectsService } from '../../shared/services/projects.service';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Project } from '../../shared/models/project.model';
import { BsModalService, BsModalRef } from 'ngx-bootstrap/modal';
import { environment } from 'src/environments/environment';
import { ThemesService } from '../../shared/services/themes.service';

@Component({
  selector: 'app-data-gate',
  templateUrl: './data-gate.component.html',
  styleUrls: ['./data-gate.component.sass']
})
export class DataGateComponent implements OnInit, OnDestroy {
  dataGateData: DataGateResponse | null = null;
  loading = true;
  error: string | null = null;
  dynamicForm: FormGroup;
  loadingRedirect = false;
  projectShortName: string = '';
  tokenIdentifier: string = '';
  sessionToken: string = '';
  projectSettings: ProjectSettings | null = null;
  filesByCategory: { [key: string]: any[] } = {};
  private ngUnsubscribe = new Subject<void>();
  salutationMale: string = '';
  salutationFemale: string = '';
  salutationValues: { index: string; name: string }[] = [];
  project: Project | null = null;
  disableFileGet: boolean = true;
  valueInDisuputeRows: any[] = [];
  disputeBoxValidationStates = new Map<number, boolean>();
  public collapseDisputeBoxes: boolean[] = [];
  public disputeBoxReadyStates = new Map<number, boolean>();
  @ViewChild('deleteConfirmModal') deleteConfirmModal!: TemplateRef<any>;
  private modalRef?: BsModalRef;
  private rowToDelete: number | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private dataGateService: DataGateService,
    private toastr: ToastrService,
    private coreService: CoreService,
    private projectService: ProjectsService,
    private translateService: TranslateService,
    private fb: FormBuilder,
    private modalService: BsModalService,
    private themeService: ThemesService
  ) {
    this.dynamicForm = this.fb.group({});
  }

  ngOnInit(): void {
    this.route.params.subscribe(params => {
      const tokenIdentifier = params['tokenIdentifier'];

      if (!tokenIdentifier) {
        this.error = 'Missing required parameters';
        this.loading = false;
        this.coreService.disableGlobalLoading();
        this.projectService.globalLoadingSignal.set(false);
        return;
      }

      this.tokenIdentifier = tokenIdentifier;
      this.sessionToken = this.coreService.getSessionToken() || '';
      this.projectSettings = this.coreService.getProjectSettings();

      this.dataGateService.getDataGateData(tokenIdentifier).subscribe({
        next: (response) => {
          this.dataGateData = response;
          if (response.success && response.result?.projectShortName) {
            this.projectShortName = response.result.projectShortName;
            this.themeService.setProjectShortName(this.projectShortName);
            this.themeService.getProjectDetails();
            this.projectService.globalLoadingSignal.set(true);
            this.projectService.getProject(this.projectShortName).pipe(
              takeUntil(this.ngUnsubscribe)
            ).subscribe({
              next: (project: Project) => {
                this.projectService.setLoading(true);

                this.translateService.get('StepLeadGeneration.Mister').pipe(takeUntil(this.ngUnsubscribe)).subscribe((res: string) => {
                  this.salutationMale = res;
                });
                this.translateService.get('StepLeadGeneration.Miss').pipe(takeUntil(this.ngUnsubscribe)).subscribe((res: string) => {
                  this.salutationFemale = res;
                });

                this.salutationValues = [
                  {index: 'Herr', name: this.salutationMale},
                  {index: 'Frau', name: this.salutationFemale}
                ];

                this.project = project;
                if (this.dataGateData?.result) {
                  // Sort Akt fields by order
                  if (this.dataGateData.result.aktFields) {
                    this.dataGateData.result.aktFields.sort((a: any, b: any) => (a.order || 0) - (b.order || 0));
                  }

                  // Sort Contact fields by order
                  if (this.dataGateData.result.contactFields) {
                    this.dataGateData.result.contactFields.sort((a: any, b: any) => (a.order || 0) - (b.order || 0));
                  }

                  // Sort Streitwert fields by order
                  if (this.dataGateData.result.streitwertsToCreate) {
                    this.dataGateData.result.streitwertsToCreate.forEach((streitwert: any) => {
                      if (streitwert.selectedFields) {
                        streitwert.selectedFields.sort((a: any, b: any) => (a.order || 0) - (b.order || 0));
                      }
                    });
                  }

                  if (this.dataGateData.result.streitwertsToUpdate) {
                    this.dataGateData.result.streitwertsToUpdate.forEach((streitwert: any) => {
                      if (streitwert.selectedFields) {
                        streitwert.selectedFields.sort((a: any, b: any) => (a.order || 0) - (b.order || 0));
                      }
                    });
                  }

                  this.projectSettings = {
                    digitalSignature: false,
                    enableCompanyField: false,
                    enableAddress: false,
                    enableMobilePhone: false,
                    step2: false,
                    step3: false,
                    step4: false,
                    redirectToStep2: false,
                    redirectToStep3: false,
                    allowPostal: false,
                    sendSms: false,
                    step1ListId: 0,
                    step2ListId: 0,
                    step3ListId: 0,
                    aktConfiguration: {
                      selectedFields: []
                    },
                    streitwertConfiguration: {
                      maxStreitwertCount: (this.dataGateData.result as any).maxStreitwertCount || 0,
                      selectedProducts: []
                    },
                    requiredFileCategories: this.dataGateData.result.requiredFileCategories || [],
                    streitwertRequiredFileCategories: this.dataGateData.result.streitwertRequiredFileCategories || []
                  };
                }
                this.coreService.enableIsLeadGenerationStep();
                this.coreService.disableGlobalLoading();
              },
              error: (err: any) => {
                this.coreService.disableGlobalLoading();
                this.projectService.globalLoadingSignal.set(false);
                this.toastr.error(err);
                if (err.status === 404) {
                  // this.router.navigate(['/404']);
                }
              }
            });
          }
          this.initializeForm();
          this.initializeStreitwerts();
          this.loading = false;
          this.coreService.disableGlobalLoading();
          this.projectService.globalLoadingSignal.set(false);
        },
        error: (error) => {
          this.error = 'Failed to load data';
          this.loading = false;
          this.toastr.error('Failed to load data', 'Error');
          console.error('Error loading data:', error);
          this.coreService.disableGlobalLoading();
          this.projectService.globalLoadingSignal.set(false);
        }
      });
    });
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  private initializeForm(): void {
    if (!this.dataGateData?.result) return;

    const formControls: { [key: string]: FormControl } = {};
    const result = this.dataGateData.result;

    // Initialize form controls for Akt fields
    result.aktFields.forEach(field => {
      // Skip fields that shouldn't be displayed but still add them to form for payload
      if (field.displayForExistingAkt === false) {
        // Add hidden control for payload purposes
        const control = new FormControl(field.valueToDisplay || '', { validators: [] });
        control.disable(); // Keep disabled for hidden fields
        formControls[field.fieldName] = control;
        return;
      }

      // For dropdown fields, find matching option based on valueToDisplay
      if ((field.type === 'boolean' || field.type === 'optionset' || field.type === 'lookup') && field.options) {
        let initialValue = null;
        if (field.valueToDisplay) {
          const matchingOption = field.options.find((opt: any) => opt.option === field.valueToDisplay);
          initialValue = matchingOption ? matchingOption.value : null;
        }
        const control = new FormControl(initialValue, { validators: field.required ? [Validators.required] : [] });
        // Only disable if there's an existing value AND updates aren't allowed
        if (field.valueToDisplay && field.availableForUpdateIfHasValue === false) {
          control.disable();
        }
        formControls[field.fieldName] = control;
      } else {
        // Build validators array based on field type
        const validators = [];
        if (field.required) {
          validators.push(Validators.required);
        }
        if (field.type === 'email') {
          validators.push(Validators.email);
        }
        if (field.maxLength) {
          validators.push(Validators.maxLength(Number(field.maxLength)));
        }

        const control = new FormControl(field.valueToDisplay || '', { validators });
        // Only disable if there's an existing value AND updates aren't allowed
        if (field.valueToDisplay && field.availableForUpdateIfHasValue === false) {
          control.disable();
        }
        formControls[field.fieldName] = control;
      }
    });

    // Initialize form controls for Contact fields
    result.contactFields.forEach(field => {
      // Always display contact fields in the form

      // For dropdown fields, find matching option based on valueToDisplay
      if ((field.type === 'boolean' || field.type === 'optionset' || field.type === 'lookup') && field.options) {
        let initialValue = null;
        if (field.valueToDisplay) {
          const matchingOption = field.options.find((opt: any) => opt.option === field.valueToDisplay);
          initialValue = matchingOption ? matchingOption.value : null;
        }
        const control = new FormControl(initialValue, { validators: field.required ? [Validators.required] : [] });
        // Only disable if there's an existing value AND updates aren't allowed
        if (field.valueToDisplay && field.availableForUpdateIfHasValue === false) {
          control.disable();
        }
        formControls[field.fieldName] = control;
      } else {
        // Build validators array based on field type
        const validators = [];
        if (field.required) {
          validators.push(Validators.required);
        }
        if (field.type === 'email') {
          validators.push(Validators.email);
        }
        if (field.maxLength) {
          validators.push(Validators.maxLength(Number(field.maxLength)));
        }

        const control = new FormControl(field.valueToDisplay || '', { validators });
        // Only disable if there's an existing value AND updates aren't allowed
        if (field.valueToDisplay && field.availableForUpdateIfHasValue === false) {
          control.disable();
        }
        formControls[field.fieldName] = control;
      }
    });

    // Initialize form controls for Streitwerts
    [...result.streitwertsToCreate, ...result.streitwertsToUpdate].forEach(streitwert => {
      if (!streitwert) return;

      streitwert.selectedFields?.forEach(field => {
        // Always add fields to form for payload purposes, but handle display logic separately
        let initialValue = null;

        if (field.valueToDisplay) {
          if (field.type === 'lookup' || field.type === 'optionset' || field.type === 'boolean') {
            const matchingOption = field.options?.find((opt: any) => opt.option === field.valueToDisplay);
            initialValue = matchingOption ? matchingOption.value : null;
          } else {
            initialValue = field.valueToDisplay;
          }
        }

        // Only add required validator if the field is required and should be displayed
        const validators = (field.required && field.displayForExistingStreitwert !== false) ? [Validators.required] : [];

        // Add maxLength validator for text fields if specified and should be displayed
        if (field.type === 'text' && field.maxLength && field.displayForExistingStreitwert !== false) {
          validators.push(Validators.maxLength(Number(field.maxLength)));
        }

        const control = new FormControl(initialValue, { validators });

        // Disable fields that shouldn't be displayed or can't be updated
        if (field.displayForExistingStreitwert === false || (field.availableForUpdateIfHasValue === false && field.type !== 'currency')) {
          control.disable();
        }

        formControls[field.fieldName] = control;
      });

      // Initialize file controls for each streitwert
      if (result.streitwertRequiredFileCategories?.length > 0) {
        result.streitwertRequiredFileCategories.forEach(category => {
          if (!category?.id) return;
          const fileControlName = `Files_${category.id}_${streitwert.streitwertId || Date.now()}`;
          formControls[fileControlName] = new FormControl([], {
            validators: category.minimumFiles > 0 ? [Validators.required] : []
          });
        });
      }
    });

    this.dynamicForm = this.fb.group(formControls);
  }

  private initializeStreitwerts(): void {
    if (!this.dataGateData?.result) return;

    // Initialize existing streitwerts
    if (this.dataGateData.result.streitwertsToUpdate && this.dataGateData.result.streitwertsToUpdate.length > 0) {
      this.valueInDisuputeRows = this.dataGateData.result.streitwertsToUpdate.map(streitwert => ({
        id: streitwert.streitwertId || Date.now(),
        value: null,
        description: '',
        streitwertNumber: streitwert.streitwertNumber,
        productId: streitwert.id,
        productName: streitwert.name,
        isNew: false,
        streitwertPostfix: streitwert.streitwertPostfix
      }));

      // Initialize validation states for existing disputeboxes as invalid
      // They will be validated by the dispute-box component
      for (let i = 0; i < this.valueInDisuputeRows.length; i++) {
        this.disputeBoxValidationStates.set(i, false);
        this.disputeBoxReadyStates.set(i, false);

        // Mark disputebox as ready after initialization delay
        setTimeout(() => {
          this.disputeBoxReadyStates.set(i, true);
        }, 500 + (i * 100)); // Stagger the ready states to prevent all loading at once
      }
    } else if (this.dataGateData.result.streitwertsToCreate?.length > 0) {
      // Only add a new streitwert if there are no existing ones
      this.addValueInDispute();
    }
  }

  public activeState(): boolean {
    // Get maxStreitwertCount from project settings, default to undefined if not set
    const maxStreitwertCount = this.projectSettings?.streitwertConfiguration?.maxStreitwertCount;

    // Check if all current dispute boxes are valid
    const allCurrentDisputeBoxesValid = this.areAllDisputeBoxesValid();

    // If maxStreitwertCount is not set, always show the button if current boxes are valid
    if (maxStreitwertCount === undefined || maxStreitwertCount === 0) {
      return allCurrentDisputeBoxesValid;
    }

    // Show the button if:
    // 1. All current dispute boxes are valid AND
    // 2. Current count is below maxStreitwertCount
    const canAddMore = this.valueInDisuputeRows.length < maxStreitwertCount;

    return allCurrentDisputeBoxesValid && canAddMore;
  }

  private areAllDisputeBoxesValid(): boolean {
    // Check if all dispute boxes are valid
    for (let i = 0; i < this.valueInDisuputeRows.length; i++) {
      if (!this.disputeBoxValidationStates.get(i)) {
        return false;
      }
    }
    return true;
  }

  public addValueInDispute(): void {
    const newRow = {
      id: Date.now(),
      value: null,
      description: '',
      streitwertNumber: this.valueInDisuputeRows.length,
      productId: this.dataGateData?.result.streitwertsToCreate[0]?.id,
      productName: this.dataGateData?.result.streitwertsToCreate[0]?.name,
      isNew: true,
      streitwertPostfix: ''
    };
    this.valueInDisuputeRows.push(newRow);
    // Initialize as invalid - let the dispute-box component determine validity
    this.disputeBoxValidationStates.set(this.valueInDisuputeRows.length - 1, false);
    this.disputeBoxReadyStates.set(this.valueInDisuputeRows.length - 1, false);

    // Mark new disputebox as ready after initialization delay
    setTimeout(() => {
      this.disputeBoxReadyStates.set(this.valueInDisuputeRows.length - 1, true);
    }, 300); // Shorter delay for new disputeboxes
  }

  public getFieldsForDisputeBox(row: any): any {
    if (!this.dataGateData?.result) return { SelectedFields: [] };

    const streitwert = row.isNew
      ? this.dataGateData.result.streitwertsToCreate?.[0]
      : this.dataGateData.result.streitwertsToUpdate?.find((s: any) => s.streitwertId === row.id);

    if (!streitwert || !streitwert.selectedFields) {
      return { SelectedFields: [] };
    }

    return {
      SelectedFields: streitwert.selectedFields
        .filter((field: any) => field.displayForExistingStreitwert !== false) // Filter out fields that shouldn't be displayed
        .map((field: any) => ({
          Type: field.type,
          FieldName: field.fieldName,
          Name: `${field.fieldName}_${row.id}`,
          SpaLabel: field.spaLabel || field.label,
          Label: field.label || field.spaLabel,
          Required: field.required || field.mandatory,
          Mandatory: field.mandatory || field.required,
          Options: field.options || [],
          Min: field.min,
          Max: field.max,
          MaxLength: field.maxLength,
          Multiselect: field.multiselect || false,
          Order: field.order || 0,
          AvailableForUpdateIfHasValue: field.availableForUpdateIfHasValue || false,
          DisplayForExistingAkt: field.displayForExistingAkt || false,
          DisplayForExistingStreitwert: field.displayForExistingStreitwert || false,
          phoneNumber: field.phoneNumber || false,
          email: field.email || false,
          url: field.url || false,
          valueToDisplay: field.valueToDisplay,
          readonly: field.availableForUpdateIfHasValue === false && field.type !== 'currency'
        }))
        .sort((a: any, b: any) => (a.Order || 0) - (b.Order || 0))
    };
  }

  public getProductNameForDisputeBox(row: any): string {
    if (row.isNew) {
      // return this.dataGateData?.result.streitwertsToCreate[0]?.name || 'Streitwert';
    }
    // return row.productName || row.name || 'Streitwert';
    return 'Anspruch';
  }

  public getTitleForDisputeBox(row: any): string {
    const productName = this.getProductNameForDisputeBox(row);
    const number = row.streitwertNumber + 1;
    const postfix = row.streitwertPostfix ? ` - ${row.streitwertPostfix}` : '';
    return `${productName} - ${number}${postfix}`;
  }

  /**
   * Returns the title for the dispute box in datagate mode for existing Streitwerts (streitwertsToUpdate).
   * If the field 'ibs_defendantid' exists in selectedFields, uses its valueToDisplay.
   * Otherwise, falls back to the product name.
   */
  public getDatagateDisputeBoxTitle(row: any): string {
    if (!row.isNew && this.dataGateData?.result?.streitwertsToUpdate) {
      const sw = this.dataGateData.result.streitwertsToUpdate.find((s: any) => s.streitwertId === row.id);
      if (sw && Array.isArray(sw.selectedFields)) {
        const defendantField = sw.selectedFields.find((f: any) => f.fieldName === 'ibs_defendantid');
        if (defendantField && defendantField.valueToDisplay) {
         // return `Anspruch - ${defendantField.valueToDisplay}`;
        }
      }
    }
    return this.getProductNameForDisputeBox(row);
  }

  onDisputeBoxValidationChange(isValid: boolean, index: number): void {
    this.disputeBoxValidationStates.set(index, isValid);

    // Mark disputebox as ready after first validation
    this.disputeBoxReadyStates.set(index, true);

    // Only auto-collapse dispute box when it becomes valid AND is not a new disputebox
    // This prevents premature collapsing during loading and validation
    if (isValid && !this.valueInDisuputeRows[index]?.isNew) {
      this.collapseDisputeBox(index);
    }
  }

  public isDisputeBoxReady(index: number): boolean {
    return this.disputeBoxReadyStates.get(index) || false;
  }

  // Method to collapse a specific dispute box
  private collapseDisputeBox(disputeBoxIndex: number): void {
    // Initialize the array if needed
    if (!this.collapseDisputeBoxes) {
      this.collapseDisputeBoxes = new Array(this.valueInDisuputeRows.length).fill(false);
    }

    // Set the collapse flag for this specific dispute box
    this.collapseDisputeBoxes[disputeBoxIndex] = true;

    // Reset the flag after a short delay to allow the component to react
    setTimeout(() => {
      this.collapseDisputeBoxes[disputeBoxIndex] = false;
    }, 100);
  }

  isFormValid(): boolean {
    // Check if form is valid by only considering enabled controls, excluding file controls
    const enabledControlsValid = Object.keys(this.dynamicForm.controls).every(controlName => {
      const control = this.dynamicForm.get(controlName);
      // Skip file controls (handled by dispute box validation) and disabled controls
      if (controlName.startsWith('Files_') || !control || control.disabled) {
        return true;
      }
      return control.valid;
    });

    const disputeBoxStates = Array.from(this.disputeBoxValidationStates.entries());
    const allDisputeBoxesValid = disputeBoxStates.every(([_, isValid]) => isValid);

    if (!enabledControlsValid) return false;
    return allDisputeBoxesValid;
  }

  clickOnNextButton(): void {
    if (!this.isFormValid()) return;

    this.loadingRedirect = true;

    // 1. Prepare AktFields
    const AktFields = (this.dataGateData?.result.aktFields || []).map(field => {
      let value = this.dynamicForm.get(field.fieldName)?.value;

      // If the control is disabled, use the original valueToDisplay
      if (this.dynamicForm.get(field.fieldName)?.disabled) {
        if ((field.type === 'boolean' || field.type === 'optionset' || field.type === 'lookup') && field.options) {
          const matchingOption = field.options.find((opt: any) => opt.option === field.valueToDisplay);
          value = matchingOption ? matchingOption.value : null;
        } else {
          value = field.valueToDisplay;
        }
      }

      // Format date fields
      if (field.type === 'date' && value) {
        const date = new Date(value);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        value = `${year}-${month}-${day}T00:00:00`;
      }

      // Format currency fields
      if (field.type === 'currency' && value) {
        if (typeof value === 'object' && value.CurrencyId && value.Value) {
          // Value is already in correct format
          value = JSON.stringify(value);
        } else if (typeof value === 'string' || typeof value === 'number') {
          // Convert to currency format using the first available currency
          const currency = field.currency?.[0];
          if (currency) {
            value = JSON.stringify({
              CurrencyId: currency.id,
              Value: value.toString()
            });
          }
        }
      }

      // For lookup, optionset, and boolean fields, format as JSON string with the option object
      if (['lookup', 'optionset', 'boolean'].includes(field.type) && value) {
        const matchingOption = field.options?.find((opt: any) => opt.value === value);
        if (matchingOption) {
          value = JSON.stringify({
            Option: matchingOption.option,
            Value: matchingOption.value,
            EntityLogicalName: matchingOption.entityLogicalName || null
          });
        }
      }

      return {
        Type: field.type,
        FieldName: field.fieldName,
        Order: field.order || 0,
        Value: value
      };
    });

    // 2. Prepare ContactFields
    const ContactFields = (this.dataGateData?.result.contactFields || []).map(field => {
      let value = this.dynamicForm.get(field.fieldName)?.value;

      // If the control is disabled, use the original valueToDisplay
      if (this.dynamicForm.get(field.fieldName)?.disabled) {
        if ((field.type === 'boolean' || field.type === 'optionset' || field.type === 'lookup') && field.options) {
          const matchingOption = field.options.find((opt: any) => opt.option === field.valueToDisplay);
          value = matchingOption ? matchingOption.value : null;
        } else {
          value = field.valueToDisplay;
        }
      }

      // Format date fields
      if (field.type === 'date' && value) {
        const date = new Date(value);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        value = `${year}-${month}-${day}T00:00:00`;
      }

      // Format currency fields
      if (field.type === 'currency' && value) {
        if (typeof value === 'object' && value.CurrencyId && value.Value) {
          // Value is already in correct format
          value = JSON.stringify(value);
        } else if (typeof value === 'string' || typeof value === 'number') {
          // Convert to currency format using the first available currency
          const currency = field.currency?.[0];
          if (currency) {
            value = JSON.stringify({
              CurrencyId: currency.id,
              Value: value.toString()
            });
          }
        }
      }

      // For lookup, optionset, and boolean fields, format as JSON string with the option object
      if (['lookup', 'optionset', 'boolean'].includes(field.type) && value) {
        const matchingOption = field.options?.find((opt: any) => opt.value === value);
        if (matchingOption) {
          value = JSON.stringify({
            Option: matchingOption.option,
            Value: matchingOption.value,
            EntityLogicalName: matchingOption.entityLogicalName || null
          });
        }
      }

      return {
        Type: field.type,
        FieldName: field.fieldName,
        Order: field.order || 0,
        Value: value
      };
    });

    // 3. Prepare StreitwertsToCreate (only for new Streitwerts)
    const StreitwertsToCreate = this.valueInDisuputeRows
      .filter(row => row.isNew)
      .map(row => {
        // Find the matching definition for this row
        const swDef = (this.dataGateData?.result.streitwertsToCreate || []).find(sw => sw.id === row.productId);
        return {
          StreitwertNumber: row.streitwertNumber,
          AllowCreation: false,
          Id: row.productId,
          LogicalName: swDef?.logicalName || 'product',
          SelectedFieldsIds: [],
          SelectedFields: (swDef?.selectedFields || []).map(f => {
            const controlName = `${f.fieldName}_${row.id}`;
            let value = this.dynamicForm.get(controlName)?.value;

            // If the control is disabled, use the original valueToDisplay
            if (this.dynamicForm.get(controlName)?.disabled) {
              if (f.type === 'lookup' || f.type === 'optionset' || f.type === 'boolean') {
                const matchingOption = f.options?.find((opt: any) => opt.option === f.valueToDisplay);
                value = matchingOption ? matchingOption.value : null;
              } else {
                value = f.valueToDisplay;
              }
            }

            // Format date fields
            if (f.type === 'date' && value) {
              const date = new Date(value);
              const year = date.getFullYear();
              const month = String(date.getMonth() + 1).padStart(2, '0');
              const day = String(date.getDate()).padStart(2, '0');
              value = `${year}-${month}-${day}T00:00:00`;
            }

            // Format currency fields
            if (f.type === 'currency' && value) {
              if (typeof value === 'object' && value.CurrencyId && value.Value) {
                // Value is already in correct format
                value = JSON.stringify(value);
              } else if (typeof value === 'string' || typeof value === 'number') {
                // Convert to currency format using the first available currency
                const currency = f.currency?.[0];
                if (currency) {
                  value = JSON.stringify({
                    CurrencyId: currency.id,
                    Value: value.toString()
                  });
                }
              }
            }

            // For lookup, optionset, and boolean fields, format as JSON string with the option object
            if (['lookup', 'optionset', 'boolean'].includes(f.type) && value) {
              const matchingOption = f.options?.find((opt: any) => opt.value === value);
              if (matchingOption) {
                value = JSON.stringify({
                  Option: matchingOption.option,
                  Value: matchingOption.value,
                  EntityLogicalName: matchingOption.entityLogicalName || null
                });
              }
            }

            return {
              Type: f.type,
              FieldName: f.fieldName,
              Order: f.order || 0,
              Multiselect: f.multiselect,
              Value: value
            };
          }),
          StreitwertPostfix: row.streitwertPostfix
        };
      });

    // 4. Prepare StreitwertsToUpdate (only for existing Streitwerts)
    const StreitwertsToUpdate = this.valueInDisuputeRows
      .filter(row => !row.isNew)
      .map(row => {
        // Find the matching definition for this row from streitwertsToUpdate
        const swDef = (this.dataGateData?.result.streitwertsToUpdate || []).find(sw => sw.streitwertId === row.id);
        return {
          StreitwertId: row.id,
          StreitwertNumber: row.streitwertNumber,
          Id: row.productId,
          LogicalName: swDef?.logicalName || 'product',
          SelectedFieldsIds: [],
          SelectedFields: (swDef?.selectedFields || []).map(f => {
            const controlName = `${f.fieldName}_${row.id}`;
            let value = this.dynamicForm.get(controlName)?.value;

            // If the control is disabled, use the original valueToDisplay
            if (this.dynamicForm.get(controlName)?.disabled) {
              if (f.type === 'lookup' || f.type === 'optionset' || f.type === 'boolean') {
                const matchingOption = f.options?.find((opt: any) => opt.option === f.valueToDisplay);
                value = matchingOption ? matchingOption.value : null;
              } else {
                value = f.valueToDisplay;
              }
            }

            // Format date fields
            if (f.type === 'date' && value) {
              const date = new Date(value);
              const year = date.getFullYear();
              const month = String(date.getMonth() + 1).padStart(2, '0');
              const day = String(date.getDate()).padStart(2, '0');
              value = `${year}-${month}-${day}T00:00:00`;
            }

            // Format currency fields
            if (f.type === 'currency' && value) {
              if (typeof value === 'object' && value.CurrencyId && value.Value) {
                // Value is already in correct format
                value = JSON.stringify(value);
              } else if (typeof value === 'string' || typeof value === 'number') {
                // Convert to currency format using the first available currency
                const currency = f.currency?.[0];
                if (currency) {
                  value = JSON.stringify({
                    CurrencyId: currency.id,
                    Value: value.toString()
                  });
                }
              }
            }

            // For lookup, optionset, and boolean fields, format as JSON string with the option object
            if (['lookup', 'optionset', 'boolean'].includes(f.type) && value) {
              const matchingOption = f.options?.find((opt: any) => opt.value === value);
              if (matchingOption) {
                value = JSON.stringify({
                  Option: matchingOption.option,
                  Value: matchingOption.value,
                  EntityLogicalName: matchingOption.entityLogicalName || null
                });
              }
            }

            return {
              Type: f.type,
              FieldName: f.fieldName,
              Order: f.order || 0,
              Multiselect: f.multiselect,
              Value: value
            };
          }),
          StreitwertPostfix: row.streitwertPostfix
        };
      });

    // 5. Compose the payload
    const payload = {
      aktFields: AktFields,
      contactFields: ContactFields,
      streitwertsToCreate: StreitwertsToCreate,
      streitwertsToUpdate: StreitwertsToUpdate,
      completeDataUpdate: true
    };

    // 6. POST to the API
    const url = `${environment.apiUrl}DataGate/${this.tokenIdentifier}`;
    this.dataGateService.postDataGateData(url, payload).subscribe({
      next: (response) => {
        this.loadingRedirect = false;
        // Navigate to thank you page for datagate
        this.router.navigate(['/registration/datagate/thank-you', this.tokenIdentifier]);
      },
      error: (err) => {
        this.loadingRedirect = false;
        console.error('DataGate API Error:', err);
        console.error('Error status:', err.status);
        console.error('Error message:', err.message);
        if (err.error) {
          console.error('Error details:', err.error);
        }
        this.toastr.error('Fehler beim Speichern der Daten');
      }
    });
  }

  refreshFiles(): void {
    // Implement file refresh logic here
  }

  getFormControl(fieldName: string): FormControl {
    return this.dynamicForm.get(fieldName) as FormControl;
  }

  public removeValueInDispute(index: number): void {
    this.rowToDelete = index;
    this.modalRef = this.modalService.show(this.deleteConfirmModal, {
      class: 'modal-sm'
    });
  }

  public confirmDelete(): void {
    if (this.rowToDelete === null || this.rowToDelete >= this.valueInDisuputeRows.length) return;

    const row = this.valueInDisuputeRows[this.rowToDelete];
    if (!row) return;

    const deletedIndex = this.rowToDelete;

    // Remove all form controls for this dispute box
    if (row.selectedFields && Array.isArray(row.selectedFields)) {
      row.selectedFields.forEach((field: any) => {
        if (field && field.fieldName) {
          this.dynamicForm.removeControl(`${field.fieldName}_${row.id}`);
        }
      });
    }

    // Remove file controls
    const fileCategories = this.projectSettings?.streitwertRequiredFileCategories;
    if (fileCategories && Array.isArray(fileCategories) && fileCategories.length > 0) {
      fileCategories.forEach((category: any) => {
        if (category && category.id) {
          this.dynamicForm.removeControl(`Files_${category.id}_${row.id}`);
        }
      });
    }

    // Remove the row
    this.valueInDisuputeRows.splice(deletedIndex, 1);

    // Create new validation states map with only valid indices
    const newValidationStates = new Map<number, boolean>();
    Array.from(this.disputeBoxValidationStates.entries())
      .filter(([index]) => index < deletedIndex) // Keep states before deleted index
      .forEach(([index, state]) => newValidationStates.set(index, state));

    Array.from(this.disputeBoxValidationStates.entries())
      .filter(([index]) => index > deletedIndex) // Move states after deleted index
      .forEach(([index, state]) => newValidationStates.set(index - 1, state));

    // Replace old validation states with new ones
    this.disputeBoxValidationStates = newValidationStates;

    this.modalRef?.hide();
    this.rowToDelete = null;
  }

  public cancelDelete(): void {
    this.modalRef?.hide();
    this.rowToDelete = null;
  }

  public hasVisibleAktFields(): boolean {
    if (!this.dataGateData?.result?.aktFields) return false;
    return this.dataGateData.result.aktFields.some(field => field.displayForExistingAkt !== false);
  }

  public hasVisibleContactFields(): boolean {
    if (!this.dataGateData?.result?.contactFields) return false;
    // Always show contact fields section if contactFields array exists and has items
    return this.dataGateData.result.contactFields.length > 0;
  }
}
